import { useState, useEffect, useImperativeHandle, RefObject, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon, MediaPreview } from '@anytag/shared-atoms';
import { useScrollElementBottom } from '@anytag/shared-hooks';
import { cn } from '@anytag/shared-utils';
import { useChatRoomContext } from '../chat-room-context';

export interface InputProps {
  loading?: boolean;
  className?: string;
  placeholder?: string;
  defaultMessage?: string;
  onSubmit: (data: { message: string; files: File[] }, onSuccess: () => void) => void;
  ref?: RefObject<HTMLTextAreaElement | null>;
}

export const Input = ({ loading, placeholder, onSubmit, defaultMessage = '', ref, ...restProps }: InputProps) => {
  const { t } = useTranslation();
  const [message, setMessage] = useState(defaultMessage);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [textAreaRef, scrollToBottom] = useScrollElementBottom<HTMLTextAreaElement>([message]);
  useImperativeHandle(ref, () => textAreaRef.current || ({} as HTMLTextAreaElement), []);
  const { isDisabledUploadFile, uploadingFiles, removeFile, clearUploadingFiles, uploadNewFiles, uploadFileConfigs } =
    useChatRoomContext();

  const isUploadFileButtonDisabled = isDisabledUploadFile || loading;
  const hasUserInputOrFile = message || uploadingFiles.length;

  useEffect(() => {
    setMessage(defaultMessage);
  }, [defaultMessage]);

  const handleSendMessage = () => {
    if (message || uploadingFiles.length || defaultMessage) {
      onSubmit?.({ message, files: uploadingFiles }, () => {
        setMessage('');
        clearUploadingFiles();
      });
    }
  };

  const handlePressEnter = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Shift + Enter for breaking line and Enter for sending message
    // https://adasiaholdings.atlassian.net/browse/AT-6195
    if (e.key === 'Enter') {
      e.preventDefault();

      // User can move the focus point somewhere then break the line. Default Enter key of textarea will handle this behavior
      // But we are custom the breakline keyboard so we need to handle it manually
      if (e.shiftKey) {
        const currentValue = e.currentTarget.value;
        const selectionEnd = e.currentTarget.selectionEnd;
        const selectionStart = e.currentTarget.selectionStart;

        // Insert breakline after the focusing point but don't set
        e.currentTarget.value = `${currentValue.substring(0, selectionStart)}\r\n${currentValue.substring(selectionEnd, currentValue.length)}`;

        // Scroll down to bottom if break line is the last line
        if (selectionEnd === currentValue.length) {
          scrollToBottom();
        }

        // Move the current focus to after new break line
        e.currentTarget.selectionEnd = selectionStart + 1;
        e.currentTarget.selectionStart = selectionStart + 1;
      } else {
        handleSendMessage();
      }
    }
  };

  return (
    <div className={cn(styles.wrapper, { 'border-base-active': isFocused })} {...restProps}>
      {uploadingFiles.length ? (
        <div className={styles.files}>
          {uploadingFiles.map((file, index) => (
            <div key={`upload-file-${index}`} className={styles.imagePreview}>
              <div className={cn(styles.removeImageIcon, 'remove-image-icon')} onClick={() => removeFile(index)}>
                <Icon size={8} icon="close" variant="white" />
              </div>
              <MediaPreview src={URL.createObjectURL(file)} className="size-16 rounded-lg" />
            </div>
          ))}
        </div>
      ) : null}

      <textarea
        ref={textAreaRef}
        value={message}
        className={styles.input}
        disabled={loading}
        autoCorrect="off"
        autoComplete="off"
        onKeyDown={handlePressEnter}
        onChange={(e) => setMessage(e.target.value)}
        placeholder={placeholder || t<string>('Placeholder.aiAssistantInput')}
        autoFocus
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
      />

      <div className="flex items-center justify-between">
        {uploadFileConfigs ? (
          <>
            <Button
              title={<label htmlFor="file-upload" />}
              variant="outline"
              onClick={() => {
                if (isUploadFileButtonDisabled) {
                  return;
                }
                inputRef.current?.click();
              }}
              disabled={isUploadFileButtonDisabled}
              className={cn(styles.uploadButton, { 'cursor-default': isUploadFileButtonDisabled })}
              prefixIcon={{
                size: 16,
                icon: 'paperclip',
                variant: isUploadFileButtonDisabled ? 'light-gray' : 'gray'
              }}
            />
            <input
              ref={inputRef}
              type="file"
              id="file-upload"
              className="hidden"
              accept={uploadFileConfigs.accept}
              multiple={uploadFileConfigs.multiple}
              disabled={isUploadFileButtonDisabled}
              onChange={(e) => {
                const files = e.target.files;

                if (files?.length) {
                  uploadNewFiles(Array.from(files));
                  /* eslint-disable */
                  // @ts-ignore
                  e.target.value = null; // Can re-upload same file
                }
              }}
            />
          </>
        ) : null}
        {loading ? (
          <Button variant="solid" className={styles.sendButton}>
            <div className="size-2 bg-white" />
          </Button>
        ) : (
          <Button
            variant="solid"
            onClick={handleSendMessage}
            loading={{ showIcon: true, status: loading }}
            disabled={!hasUserInputOrFile}
            prefixIcon={{ icon: 'arrow-up', size: 11, variant: 'white' }}
            className={cn(styles.sendButton, {
              'cursor-default !border-black-30 !bg-black-30': !hasUserInputOrFile
            })}
          />
        )}
      </div>
    </div>
  );
};

const styles = {
  wrapper: cn(
    'm-4 mt-0 box-border min-h-10 w-[calc(100%-2rem)] flex-none rounded-xl border-base-black-40 bg-white p-3',
    'shadow-[0px_2px_8px_0px_rgba(16,18,20,0.12)]'
  ),
  input: cn(
    'max-h-87.5 w-full flex-1 resize-none overflow-y-auto bg-inherit text-base leading-[140%] font-normal text-primary',
    'placeholder:text-placeholder',
    '[appearance:textfield] [moz-appearance:textfield] [&::-webkit-inner-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none'
  ),
  sendButton: cn('h-6 min-w-6 flex-none self-end rounded-full p-0'),
  files: cn('mb-3 flex max-h-32.5 flex-wrap gap-2 border-b-black-20 pb-3'),
  uploadButton: cn('h-6 w-6 flex-none gap-0 self-end rounded-full border-none p-0', 'hover:bg-black-10'),
  imagePreview: cn('relative', 'hover:[&_.remove-image-icon]:flex'),
  removeImageIcon: cn(
    'absolute top-0 right-0 z-10 box-border size-4 cursor-pointer items-center justify-center rounded-full bg-black-90',
    'hidden translate-x-[40%] -translate-y-[40%] ring-1 ring-white'
  )
};
